import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api, API_BASE } from '../lib/api';
import { getToken } from '../lib/tokenStorage';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Wallet, RefreshCw, Download, Trash2, Euro, ShieldAlert, Search, Filter, CalendarDays } from 'lucide-react';

// ★ 2026-08-04 (Fase 33.1) — Pagamenti Chiusura
// Ammin CEO vedono tutto e possono esportare/eliminare.
// Altri utenti vedono solo i propri pagamenti.

const COUNTRY_FLAG = { albania: '🇦🇱', indonesia: '🇮🇩', thailandia: '🇹🇭' };

export default function PaymentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [methodQ, setMethodQ] = useState('');
  const [search, setSearch] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      if (methodQ.trim()) params.method = methodQ.trim();
      const { data } = await api.get('/payments', { params });
      setItems(data.items || []);
      setTotalAmount(data.total_amount_eur || 0);
      setIsAdmin(!!data.is_admin);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('paymentsPage.loadError', 'Errore caricamento pagamenti'));
    } finally { setLoading(false); }
  }, [fromDate, toDate, methodQ]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      (p.lead_name || '').toLowerCase().includes(q)
      || (p.lead_email || '').toLowerCase().includes(q)
      || (p.proposal_snapshot?.name || '').toLowerCase().includes(q)
      || (p.closed_by?.email || '').toLowerCase().includes(q)
      || (p.closed_by?.name || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const doExport = () => {
    const token = getToken();
    const url = new URL(`${API_BASE}/payments/export.csv`);
    if (fromDate) url.searchParams.set('from', fromDate);
    if (toDate) url.searchParams.set('to', toDate);
    if (methodQ.trim()) url.searchParams.set('method', methodQ.trim());
    fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('Export failed');
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'proposal_payments.csv';
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      })
      .catch(() => toast.error(t('paymentsPage.exportError', 'Errore export CSV')));
  };

  const doDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/payments/${deleteItem.id}`);
      toast.success(t('paymentsPage.deleted', 'Pagamento eliminato'));
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('paymentsPage.deleteError', 'Errore eliminazione'));
    }
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setMethodQ('');
    setSearch('');
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso.length > 10 ? iso : iso + 'T00:00:00');
      return d.toLocaleDateString(i18n.language?.startsWith('es') ? 'es-ES' : i18n.language?.startsWith('en') ? 'en-GB' : 'it-IT');
    } catch { return iso; }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-6 h-6 text-[hsl(var(--primary))]" />
          <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">{t('paymentsPage.title', 'Pagamenti')}</h1>
          <Badge variant="outline" className="text-[10px]">{items.length} {t('paymentsPage.records', 'record')}</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={load} data-testid="payments-refresh">
            <RefreshCw className="w-4 h-4 mr-1" />{t('paymentsPage.refresh', 'Aggiorna')}
          </Button>
          <Button size="sm" onClick={doExport} data-testid="payments-export">
            <Download className="w-4 h-4 mr-1" />{t('paymentsPage.exportCsv', 'Esporta CSV')}
          </Button>
        </div>
      </div>

      {!isAdmin && (
        <Alert className="border-amber-300 bg-amber-50/40" data-testid="payments-scope-alert">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>{t('paymentsPage.limitedView', 'Visualizzazione limitata')}</AlertTitle>
          <AlertDescription>
            {t('paymentsPage.limitedViewDesc', 'Vedi solo i pagamenti che hai chiuso tu. Gli admin CEO vedono tutti i pagamenti del team.')}
          </AlertDescription>
        </Alert>
      )}

      {/* Filtri */}
      <Card>
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase">{t('paymentsPage.from', 'Da')}</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 h-9"
              data-testid="payments-from-date"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase">{t('paymentsPage.to', 'A')}</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 h-9"
              data-testid="payments-to-date"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase">{t('paymentsPage.methodPartial', 'Metodo (parziale)')}</Label>
            <Input
              value={methodQ}
              onChange={(e) => setMethodQ(e.target.value)}
              placeholder={t('paymentsPage.methodPlaceholder', 'Bonifico, rate…')}
              className="mt-1 h-9"
              data-testid="payments-method-filter"
            />
          </div>
          <div className="relative">
            <Label className="text-[10px] uppercase">{t('paymentsPage.search', 'Cerca')}</Label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-7 h-9"
                placeholder={t('paymentsPage.searchPlaceholder', 'Lead, proposta, chi ha chiuso…')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="payments-search"
              />
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9" data-testid="payments-clear-filters">
            <Filter className="w-4 h-4 mr-1" />{t('paymentsPage.resetFilters', 'Reset filtri')}
          </Button>
        </CardContent>
      </Card>

      {/* Totale */}
      <Card className="bg-emerald-50/60 border-emerald-200">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-emerald-900">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700">{t('paymentsPage.totalFiltered', 'Totale nel periodo filtrato')}</div>
            <div className="text-2xl font-bold flex items-center gap-1" data-testid="payments-total">
              <Euro className="w-5 h-5" />
              {Number(totalAmount).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {filtered.length} {t('paymentsPage.records', 'record')} {items.length !== filtered.length ? `(${t('paymentsPage.ofTotal', { defaultValue: 'su {{count}} totali', count: items.length })})` : ''}
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Wallet className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {t('paymentsPage.emptyFiltered', 'Nessun pagamento nel periodo/filtri selezionati.')}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface-2)] border-b border-border text-left">
                <tr>
                  <th className="px-3 py-2 text-[10px] uppercase text-muted-foreground">{t('paymentsPage.thDate', 'Data')}</th>
                  <th className="px-3 py-2 text-[10px] uppercase text-muted-foreground">Lead</th>
                  <th className="px-3 py-2 text-[10px] uppercase text-muted-foreground">{t('paymentsPage.thProposal', 'Proposta')}</th>
                  <th className="px-3 py-2 text-[10px] uppercase text-muted-foreground text-right">{t('paymentsPage.thAmount', 'Importo')}</th>
                  <th className="px-3 py-2 text-[10px] uppercase text-muted-foreground">{t('paymentsPage.thMethod', 'Metodo')}</th>
                  <th className="px-3 py-2 text-[10px] uppercase text-muted-foreground">{t('paymentsPage.thClosedBy', 'Chiuso da')}</th>
                  {isAdmin && <th className="px-3 py-2 w-24 text-[10px] uppercase text-muted-foreground text-center">{t('paymentsPage.thActions', 'Azioni')}</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const snap = p.proposal_snapshot || {};
                  const closer = p.closed_by || {};
                  return (
                    <tr key={p.id} className="border-b border-border hover:bg-[var(--surface-2)]/60" data-testid={`payment-row-${p.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap flex items-center gap-1">
                        <CalendarDays className="w-3 h-3 text-muted-foreground" />
                        {fmtDate(p.paid_at)}
                      </td>
                      <td className="px-3 py-2">
                        {p.lead_id ? (
                          <Link to={`/app/leads/${p.lead_id}`} className="text-[hsl(var(--primary))] underline">
                            {p.lead_name || '—'}
                          </Link>
                        ) : (p.lead_name || '—')}
                        <div className="text-[10px] text-muted-foreground">{p.lead_email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{snap.name || '—'}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {COUNTRY_FLAG[snap.country] || ''} {snap.country || ''} · {snap.path || ''}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700 whitespace-nowrap">
                        <Euro className="w-3 h-3 inline mr-0.5" />
                        {Number(p.amount_eur || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 max-w-[240px]">
                        <div className="line-clamp-2 text-xs">{p.payment_method || '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{closer.name || closer.email || '—'}</div>
                        {closer.name && closer.email && (
                          <div className="text-[10px] text-muted-foreground">{closer.email}</div>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            onClick={() => setDeleteItem(p)}
                            data-testid={`payment-delete-${p.id}`}
                            aria-label={t('paymentsPage.deletePayment', 'Elimina pagamento')}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            {t('paymentsPage.delete', 'Elimina')}
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!deleteItem} onOpenChange={(v) => !v && setDeleteItem(null)}>
        <AlertDialogContent data-testid="payment-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              {t('paymentsPage.deleteConfirmTitle', 'Eliminare questo pagamento?')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {deleteItem && (
                  <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">{t('paymentsPage.thAmount', 'Importo')}</span>
                      <span className="font-serif text-lg font-semibold text-red-700">
                        <Euro className="w-3.5 h-3.5 inline mr-0.5" />
                        {Number(deleteItem.amount_eur || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">{t('paymentsPage.client', 'Cliente')}</span>
                      <span className="font-medium">{deleteItem.lead_name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">{t('paymentsPage.thProposal', 'Proposta')}</span>
                      <span className="font-medium">{deleteItem.proposal_snapshot?.name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">{t('paymentsPage.thDate', 'Data')}</span>
                      <span>{fmtDate(deleteItem.paid_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground">{t('paymentsPage.thClosedBy', 'Chiuso da')}</span>
                      <span>{deleteItem.closed_by?.name || deleteItem.closed_by?.email || '—'}</span>
                    </div>
                    {deleteItem.payment_method && (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs uppercase text-muted-foreground">{t('paymentsPage.thMethod', 'Metodo')}</span>
                        <span className="text-xs text-right max-w-[280px]">{deleteItem.payment_method}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="text-red-600 font-medium text-sm">
                  {t('paymentsPage.irreversible', 'L\u2019operazione è irreversibile. Il pagamento verrà rimosso dai KPI e dalla Dashboard Incassi.')}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payment-delete-cancel">{t('paymentsPage.cancel', 'Annulla')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="payment-delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t('paymentsPage.confirmDelete', 'Sì, elimina pagamento')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
